import { normalizeAdvancedRule, parseRuleObject } from './advancedConfigRuleUtils';

const truth = value => [true, 1, 'true', '1'].includes(value);
const number = value => {
  if (value === '' || value == null || !Number.isFinite(Number(value))) throw new Error('A required numeric input is missing or invalid.');
  return Number(value);
};
const compare = (operator, left, right) => {
  switch (operator) {
    case 'eq': return left === right;
    case 'ne': return left !== right;
    case 'gt': return left > right;
    case 'gte': return left >= right;
    case 'lt': return left < right;
    case 'lte': return left <= right;
    default: throw new Error(`Unsupported comparison: ${operator}`);
  }
};

function operand(value, row, rows, depth) {
  if (!value || depth > 20) throw new Error('Invalid or excessively nested operand.');
  if (value.type === 'constant') return number(value.value);
  if (value.type === 'fieldNumber') return number(row?.inputs?.[value.fieldId]);
  if (value.type === 'fieldValueId') return number(row?.selections?.[value.fieldId]);
  if (value.type === 'aggregate') {
    if (value.collection !== 'pumpRows' || !['sum', 'count'].includes(value.operation)) throw new Error('Unsupported aggregate.');
    const matching = rows.filter(candidate => !value.filter || condition(value.filter, candidate, rows, depth + 1));
    return value.operation === 'count' ? matching.length : matching.reduce((sum, candidate) => sum + number(candidate.inputs[value.fieldId]), 0);
  }
  throw new Error(`Unsupported operand: ${value.type}`);
}

function condition(value, row, rows, depth = 0) {
  if (!value || depth > 20) throw new Error('Invalid or excessively nested condition.');
  if (Object.prototype.hasOwnProperty.call(value, 'all') || Object.prototype.hasOwnProperty.call(value, 'any')) {
    const key = Object.prototype.hasOwnProperty.call(value, 'all') ? 'all' : 'any';
    if (!Array.isArray(value[key]) || Object.keys(value).length !== 1) throw new Error('Conditions require a single all/any array.');
    // Evaluate every branch so malformed or incomplete operands cannot hide behind short-circuiting.
    const matches = value[key].map(child => condition(child, row, rows, depth + 1));
    return key === 'all' ? matches.every(Boolean) : matches.some(Boolean);
  }
  return compare(value.operator, operand(value.left, row, rows, depth + 1), operand(value.right, row, rows, depth + 1));
}

export function evaluateAdvancedRules({ rules = [], stationId, rows = [], canSetValue = () => true }) {
  const original = rows.map(row => ({ ...row, selections: { ...row.selections }, inputs: { ...row.inputs } }));
  let working = original.map(row => ({ ...row, selections: { ...row.selections } }));
  const errors = [];
  const prepared = [];
  for (const raw of rules) {
    const rule = normalizeAdvancedRule(raw);
    if (!truth(rule.IsEnabled) || truth(rule.Deleted)) continue;
    try {
      if (Number(rule.SchemaVersion) !== 1) throw new Error('Unsupported schema version.');
      const scope = parseRuleObject(rule.ApplicabilityJson, 'Applicability');
      if (Object.keys(scope).some(key => !['allStations', 'stationIds', 'excludedStationIds'].includes(key))) throw new Error('Unsupported applicability parameters.');
      for (const key of ['stationIds', 'excludedStationIds']) {
        if (scope[key] !== undefined && (!Array.isArray(scope[key]) || scope[key].some(id => !Number.isInteger(id) || id <= 0))) throw new Error('Invalid station IDs.');
      }
      if (scope.allStations !== true && !scope.stationIds?.length) continue;
      if (!(Number(stationId) > 0)) throw new Error('A station type must be resolved before rules can run.');
      if (scope.excludedStationIds?.includes(Number(stationId))) continue;
      if (scope.allStations !== true && !scope.stationIds?.includes(Number(stationId))) continue;
      if (!['EachPumpRow', 'PumpTable'].includes(rule.EvaluationScope)) throw new Error('Unsupported evaluation scope.');
      if (!['SetFieldValue', 'Validate'].includes(rule.ActionType)) throw new Error('Unsupported action.');
      prepared.push({ ...rule, when: parseRuleObject(rule.ConditionsJson, 'Conditions'), action: parseRuleObject(rule.ActionParametersJson, 'Action') });
    } catch (error) { errors.push({ ruleId: rule.Id, message: `${rule.Name}: ${error.message}` }); }
  }
  prepared.sort((a, b) => a.Priority - b.Priority || a.Id - b.Id);
  const report = (rule, error, row) => errors.push({ ruleId: rule.Id, rowKey: row?.key, message: `${rule.Name}${row ? ` (row ${rows.findIndex(item => item.key === row.key) + 1})` : ''}: ${error.message}` });
  const seen = new Set();
  for (let pass = 0; pass < 20; pass += 1) {
    const signature = JSON.stringify(working.map(row => row.selections));
    if (seen.has(signature)) { errors.push({ message: 'Conflicting automatic rules caused a cycle. No automatic changes were applied.' }); break; }
    seen.add(signature);
    const changes = new Map();
    for (const rule of prepared.filter(item => item.ActionType === 'SetFieldValue')) {
      try {
        const { fieldId, fieldValueId } = rule.action;
        if (!Number.isInteger(fieldId) || fieldId <= 0 || !Number.isInteger(fieldValueId) || fieldValueId <= 0) throw new Error('Invalid automatic target field/value.');
        if (rule.EvaluationScope !== 'EachPumpRow') throw new Error('Automatic field actions require EachPumpRow scope.');
        for (const row of working) {
          if (!condition(rule.when, row, working)) continue;
          if (!canSetValue(row, fieldId, fieldValueId)) throw new Error('The target value is unavailable or disabled for this pump row.');
          const key = `${row.key}:${fieldId}`;
          if (changes.has(key) && changes.get(key).fieldValueId !== fieldValueId) throw new Error('Conflicting automatic values target the same field.');
          changes.set(key, { rowKey: row.key, fieldId, fieldValueId });
        }
      } catch (error) { report(rule, error); }
    }
    if (errors.length) break;
    for (const change of changes.values()) {
      working.find(row => row.key === change.rowKey).selections[change.fieldId] = change.fieldValueId;
    }
    if (JSON.stringify(working.map(row => row.selections)) === signature) break;
    if (pass === 19) errors.push({ message: 'Automatic rules did not stabilize. No automatic changes were applied.' });
  }
  if (errors.length) working = original;
  for (const rule of prepared.filter(item => item.ActionType === 'Validate')) {
    for (const row of rule.EvaluationScope === 'EachPumpRow' ? working : [null]) {
      try {
        if (condition(rule.when, row, working) && !condition(rule.action, row, working)) report(rule, new Error(rule.Message || 'Constraint not satisfied.'), row);
      } catch (error) { report(rule, error, row); }
    }
  }
  const updates = working.flatMap((row, index) => Object.entries(row.selections).filter(([id, value]) => original[index].selections[id] !== value).map(([fieldId, fieldValueId]) => ({ rowKey: row.key, fieldId: Number(fieldId), fieldValueId })));
  return { rows: working, updates, errors, isValid: errors.length === 0 };
}
